import type {TRuleset} from '@kapitalist/contract/ruleset'
import {MAX_CARDS_IN_HAND} from '@kapitalist/contract/ruleset'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import {compact} from '@kapitalist/utils/falsiness'
import * as M from '@kapitalist/utils/Map'
import * as S from '@kapitalist/utils/Set'
import {isInstance} from '@kapitalist/utils/types'
import type {
  DealBreakerCard,
  DebtCollectorCard,
  ForcedDealCard,
  SlyDealCard,
} from '~/models/game/Cards/ActionCard'
import {JustSayNoCard} from '~/models/game/Cards/ActionCard'
import type {
  DirectionalRentCard,
  UniversalRentCard,
} from '~/models/game/Cards/RentCard'
import type GameData from '~/models/game/GameData'
import type GamePosition from '~/models/game/GamePosition'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type InProgressMajorAction from '~/models/game/InProgressMajorAction'
import type MajorActionDealBreaker from '~/models/game/MajorActions/MajorActionDealBreaker'
import type MajorActionForcedDeal from '~/models/game/MajorActions/MajorActionForcedDeal'
import type MajorActionSlyDeal from '~/models/game/MajorActions/MajorActionSlyDeal'
import MinorActionPromptDealBreaker from '~/models/game/MinorActionPrompts/MinorActionPromptDealBreaker'
import MinorActionPromptForcedDeal, {
  possibleDestinationNeighborhoodsForForcedDeal,
} from '~/models/game/MinorActionPrompts/MinorActionPromptForcedDeal'
import MinorActionPromptJustSayNo from '~/models/game/MinorActionPrompts/MinorActionPromptJustSayNo'
import MinorActionPromptPaySomething from '~/models/game/MinorActionPrompts/MinorActionPromptPaySomething'
import MinorActionPromptSlyDeal from '~/models/game/MinorActionPrompts/MinorActionPromptSlyDeal'
import {
  switchOnMajorAction,
  switchOnMajorActionAllowsMinorAction,
  type AnyMinorActionPrompt,
  type AnyMinorActionPromptPaySomething,
  type AnySpecialPropertyCard,
} from '~/models/game/types'

// depending on the game state, the player can click one, many, or no cards already
// played on the table or in their hand as a part of the action they wish to take.
// e.g. if they are being asked to pay rent, they can choose multiple cards, but only
// those with a cash value. TClickableCardStateV2 carries that information.
type TClickableCardV2Mode =
  | 'awaitingYourMajorAction' // 'your' turn, rotate SpecialPropertyCards or play a card from your hand
  | 'paySomething' // choose the cards to pay a rent/etc charged against you
  | 'acceptOrJustSayNo' // someone attacked you or JSN'd your attack. accept or play a JSN.
  | 'chooseNeighborhood' // choose where a force-dealt card lands (or JSN), incl. the JSN battle over that Forced Deal
  | 'none' // no cards on the table can/need to be clicked by the user atm. includes spectators (can't click anything).
type TClickableCardStateV2Base = {
  mode: TClickableCardV2Mode
  rules: TRuleset // needed for clickableCardIdsFor
}
interface IClickableCardStateV2YourTurn extends TClickableCardStateV2Base {
  mode: 'awaitingYourMajorAction'
  clickableCardsInHandIds: Set<string>
  clickableCardsInMyPortfolioIds: Set<string>
  // NB: this is one of the clickableCardsInHandIds, sometimes the Sly/Forced deal card below, other times
  // a rent/etc other card in my hand
  selectedCardInHandId: string | undefined
  selectedCardInMyPortfolioId: string | undefined
  playerGamePosition: PlayerGamePosition // TODO do we need this? sly/forced deal have it b/c this has it.

  consideringMajorAction:
    | {
        // the player needs to select the card to steal
        type: 'slyDeal'
        card: SlyDealCard
        requestableCardIds: Set<string>
        selectedCardToRequestId: string | undefined
      }
    | {
        // the player needs to select the cards to trade
        type: 'forcedDeal'
        card: ForcedDealCard
        requestableCardIds: Set<string>
        offerableCardIds: Set<string>
        selectedCardToOfferId: string | undefined
        selectedCardToRequestId: string | undefined
      }
    | {
        // the player needs to choose the target player
        type: 'debtCollector'
        card: DebtCollectorCard
        clickablePlayerIds: Set<string>
        selectedPlayerId: string | undefined
      }
    | {
        // the player needs to choose a set to rent from & a player to rent
        type: 'directionalRent'
        card: DirectionalRentCard
        clickablePortfolioNeighborhoodIds: Set<string>
        selectedPortfolioNeighborhoodId: string | undefined
        clickablePlayerIds: Set<string>
        selectedPlayerId: string | undefined
      }
    | {
        type: 'needsNeighborhood'
        card:
          | UniversalRentCard // choose a set to rent from
          | DealBreakerCard // choose a set to deal break
          | AnySpecialPropertyCard // play a dual or PWC property to one of your existing neighborhoods, or move one
        clickablePortfolioNeighborhoodIds: Set<string>
        selectedPortfolioNeighborhoodId: string | undefined
      }
    | {
        type: 'discard'
        selectedCardsToDiscardIds: Set<string>
      }
    | undefined
}
interface IClickableCardStateV2PaySomething extends TClickableCardStateV2Base {
  mode: 'paySomething'
  clickableCardIds: Set<string> // your cards with cash value, and your JSNs
  selectedCardIds: Set<string>
  inProgressMajorAction: InProgressMajorAction
  minorActionPrompt:
    | AnyMinorActionPromptPaySomething
    // a JSN was played on your JSN of the initial payment demand
    | MinorActionPromptJustSayNo
}
interface IClickableCardStateV2AcceptOrJustSayNo extends TClickableCardStateV2Base {
  mode: 'acceptOrJustSayNo'
  clickableCardIds: Set<string> // your JSNs
  selectedCardInHandId: string | undefined
  inProgressMajorAction: InProgressMajorAction // necessary for e.g. MinorAction.validResponses later
  minorActionPrompt:
    | MinorActionPromptDealBreaker
    | MinorActionPromptSlyDeal
    | MinorActionPromptJustSayNo
}
interface IClickableCardStateV2ChooseNeighborhood extends TClickableCardStateV2Base {
  mode: 'chooseNeighborhood'
  clickablePortfolioNeighborhoodIds: Set<string>
  selectedPortfolioNeighborhoodId: string | undefined

  for:
    | MinorActionPromptForcedDeal // choose the destination PN for a force-dealt card
    | MinorActionPromptJustSayNo // a JSN was played on your JSN of a Forced Deal, same as MinorActionPromptForcedDeal
}
interface IClickableCardStateV2None extends TClickableCardStateV2Base {
  mode: 'none' // added for spectators, also represents players who can't/don't need to take action atm
  inProgressMajorAction: InProgressMajorAction | undefined
  pendingMinorActionForMe: AnyMinorActionPrompt | undefined
}
export type TClickableCardStateV2 =
  | IClickableCardStateV2YourTurn
  | IClickableCardStateV2PaySomething
  | IClickableCardStateV2AcceptOrJustSayNo
  | IClickableCardStateV2ChooseNeighborhood
  | IClickableCardStateV2None

export const maybeMutateClickableCardState = (
  gameData: GameData,
  rules: TRuleset,
  previousClickableCardState: TClickableCardStateV2
): TClickableCardStateV2 => {
  const {currentGamePosition, playerGamePosition} = gameData
  if (!playerGamePosition) {
    // TODO not sure if/how the inProgressMajorAction here should work for spectators, we'll see. added it to the None
    //  type for convenience here in this file for now
    return {
      mode: 'none',
      inProgressMajorAction: currentGamePosition.inProgressMajorAction,
      pendingMinorActionForMe: undefined,
      rules,
    }
  }

  const {currentPlayer} = playerGamePosition

  if (playerGamePosition.inProgressMajorAction) {
    // there is an active/pending minorAction. if the action is on me, prompt me, else,
    // I can't do anything right now.
    const pendingMinorActionForMe =
      playerGamePosition.inProgressMajorAction.pendingMinorActions.find(
        pma => pma.minorActorPlayerId === currentPlayer.id
      )

    if (pendingMinorActionForMe) {
      // DRY builder for both an initial forcedDeal and a twice-JSN'd one's `clickableCardState`,
      // which are the same because a double-JSN'd forcedDeal is treated just like a brand new one
      const chooseNeighborhoodStateForForcedDeal = (
        forcedDeal: MajorActionForcedDeal | MinorActionPromptForcedDeal,
        forPrompt: MinorActionPromptForcedDeal | MinorActionPromptJustSayNo
      ): TClickableCardStateV2 => {
        // choose the destination for a forced dealt card
        const clickablePortfolioNeighborhoodIds = new Set(
          possibleDestinationNeighborhoodsForForcedDeal({
            offeredCard: forcedDeal.offeredCard,
            requestedCard: forcedDeal.requestedCard,
            targetedPlayer: currentPlayer,
          }).map(pn => pn.id)
        )

        return {
          mode: 'chooseNeighborhood',
          clickablePortfolioNeighborhoodIds,
          selectedPortfolioNeighborhoodId:
            previousClickableCardState.mode === 'chooseNeighborhood'
              ? S.echoIfHas(
                  previousClickableCardState.selectedPortfolioNeighborhoodId,
                  clickablePortfolioNeighborhoodIds
                )
              : // NB: if there is only one neighborhood to choose from, autoselect it for convenience
                clickablePortfolioNeighborhoodIds.size === 1
                ? S.first(clickablePortfolioNeighborhoodIds)
                : undefined,
          for: forPrompt,
          rules,
        }
      }

      if (pendingMinorActionForMe instanceof MinorActionPromptPaySomething) {
        const clickableCardIds = currentPlayer.portfolio
          .allValuableCards()
          .concat(currentPlayer.hand.cards.filter(isInstance(JustSayNoCard)))
          .map(c => c.id)

        return {
          mode: 'paySomething',
          clickableCardIds: new Set(clickableCardIds),
          selectedCardIds: new Set(),
          inProgressMajorAction: playerGamePosition.inProgressMajorAction,
          minorActionPrompt: pendingMinorActionForMe,
          rules,
        }
      } else if (
        pendingMinorActionForMe instanceof MinorActionPromptDealBreaker ||
        pendingMinorActionForMe instanceof MinorActionPromptSlyDeal
      ) {
        const clickableCardIds = new Set(
          currentPlayer.hand.cards.filter(isInstance(JustSayNoCard)).map(c => c.id)
        )

        return {
          mode: 'acceptOrJustSayNo',
          clickableCardIds,
          selectedCardInHandId: undefined,
          inProgressMajorAction: playerGamePosition.inProgressMajorAction,
          minorActionPrompt: pendingMinorActionForMe,
          rules,
        }
      } else if (pendingMinorActionForMe instanceof MinorActionPromptForcedDeal) {
        return chooseNeighborhoodStateForForcedDeal(
          pendingMinorActionForMe,
          pendingMinorActionForMe
        )
      } else if (pendingMinorActionForMe instanceof MinorActionPromptJustSayNo) {
        const jsnPrompt = pendingMinorActionForMe
        const {inProgressMajorAction} = playerGamePosition

        const jsnCardIds = new Set(
          currentPlayer.hand.cards.filter(isInstance(JustSayNoCard)).map(c => c.id)
        )

        const acceptOrJustSayNoState = (): TClickableCardStateV2 => ({
          mode: 'acceptOrJustSayNo',
          inProgressMajorAction,
          selectedCardInHandId:
            previousClickableCardState.mode === 'acceptOrJustSayNo'
              ? S.echoIfHas(
                  previousClickableCardState.selectedCardInHandId,
                  jsnCardIds
                )
              : undefined,
          rules,
          minorActionPrompt: jsnPrompt,
          clickableCardIds: jsnCardIds,
        })

        const paySomethingState = (): TClickableCardStateV2 => ({
          mode: 'paySomething',
          clickableCardIds: new Set(
            currentPlayer.portfolio
              .allValuableCards()
              .concat(currentPlayer.hand.cards.filter(isInstance(JustSayNoCard)))
              .map(c => c.id)
          ),
          selectedCardIds: new Set(),
          inProgressMajorAction,
          minorActionPrompt: jsnPrompt,
          rules,
        })

        // The major actor answering a JSN can only acquiesce or fire back another JSN.
        if (
          currentGamePosition.majorActorPlayer.id === jsnPrompt.minorActorPlayerId
        ) {
          return acceptOrJustSayNoState()
        }

        // The original victim answering the second JSN as if it were the original attack
        return switchOnMajorActionAllowsMinorAction<TClickableCardStateV2>({
          whenBirthday: paySomethingState,
          whenDebtCollector: paySomethingState,
          whenDirectionalRent: paySomethingState,
          whenDoubleTheRent: paySomethingState,
          whenUniversalRent: paySomethingState,
          whenDealBreaker: acceptOrJustSayNoState,
          whenSlyDeal: acceptOrJustSayNoState,
          whenForcedDeal: fd => chooseNeighborhoodStateForForcedDeal(fd, jsnPrompt),
        })(inProgressMajorAction.majorAction)
      } else {
        return unreachableCase(pendingMinorActionForMe)
      }
    } else {
      return {
        mode: 'none',
        inProgressMajorAction: playerGamePosition.inProgressMajorAction,
        pendingMinorActionForMe,
        rules,
      }
    }
  }

  // seems like nothing else applies, so defaults apply based on whether it is your turn
  if (playerGamePosition?.isMyMajorTurn()) {
    switch (previousClickableCardState.mode) {
      // Our own major turn, no attack pending: re-derive the turn state from scratch whatever the
      // prior mode was. The attack-response modes (chooseNeighborhood / acceptOrJustSayNo /
      // paySomething) are all resolved by now and must not carry into our turn - e.g. a
      // chooseNeighborhood left over from accepting a Forced Deal that ended the attacker's turn.
      case 'chooseNeighborhood':
      case 'acceptOrJustSayNo':
      case 'none':
      case 'paySomething':
      case 'awaitingYourMajorAction': {
        const availableTableMoves = currentPlayer.availableMovesFromTableByCardId(
          rules,
          playerGamePosition
        )

        // NB: can only discard if you have more than the allowed number of cards
        const clickableCardsInHandIds = ((): Set<string> => {
          if (
            (playerGamePosition.play === 'discardPlay' ||
              (previousClickableCardState.mode === 'awaitingYourMajorAction' &&
                previousClickableCardState.consideringMajorAction?.type ===
                  'discard')) &&
            currentPlayer.hand.cards.length <= MAX_CARDS_IN_HAND
          ) {
            return new Set()
          }
          return S.create(currentPlayer.hand.cards.map(c => c.id))
        })()

        const clickableCardsInMyPortfolioIds = M.keySet(
          M.filter(arr => !!arr.length, availableTableMoves)
        )

        const isTryingFreeAction =
          previousClickableCardState.mode === 'awaitingYourMajorAction' &&
          previousClickableCardState.consideringMajorAction?.type ===
            'needsNeighborhood'

        type TConsideringDiscardAction =
          IClickableCardStateV2YourTurn['consideringMajorAction'] & {
            type: 'discard'
          }
        // if you have no more plays left, we usually want to force you into the "considering discard" action
        // notable exception is if the player is trying to rotate cards (isTryingFreeAction).
        // (other, simpler free actions like moving residences to cash are already allowed in `type: 'discard'`, whereas
        // rotating cards needs a secondary UI that has to be explicitly allowed here)
        const forcedDiscardAction: TConsideringDiscardAction | undefined =
          playerGamePosition.play === 'discardPlay' && !isTryingFreeAction
            ? {
                type: 'discard',
                selectedCardsToDiscardIds:
                  previousClickableCardState.mode === 'awaitingYourMajorAction' &&
                  previousClickableCardState.consideringMajorAction?.type ===
                    'discard'
                    ? S.intersection(
                        clickableCardsInHandIds,
                        previousClickableCardState.consideringMajorAction
                          .selectedCardsToDiscardIds
                      )
                    : new Set(),
              }
            : undefined

        return {
          mode: 'awaitingYourMajorAction',
          rules,
          clickableCardsInHandIds,
          clickableCardsInMyPortfolioIds,

          // just in case of MDCs or other weirdness, I guess checking if the sets of selectable / selected cards
          // are still up to date doesn't hurt? not sure how necessary this is.
          selectedCardInHandId:
            previousClickableCardState.mode === 'awaitingYourMajorAction'
              ? S.echoIfHas(
                  previousClickableCardState.selectedCardInHandId,
                  clickableCardsInHandIds
                )
              : undefined,
          selectedCardInMyPortfolioId:
            previousClickableCardState.mode === 'awaitingYourMajorAction'
              ? S.echoIfHas(
                  previousClickableCardState.selectedCardInMyPortfolioId,
                  clickableCardsInMyPortfolioIds
                )
              : undefined,
          playerGamePosition,
          consideringMajorAction:
            forcedDiscardAction ??
            (previousClickableCardState.mode === 'awaitingYourMajorAction'
              ? previousClickableCardState.consideringMajorAction
              : undefined),
          // consideringMajorAction: safely(previousClickableCardState.consideringMajorAction, cma => {
          //   switch (cma.type) {
          //     case 'forcedDeal':
          //       return cma // TODO
          //     case 'slyDeal':
          //       return {
          //
          //       }
          //   }
          // })
        }
      }
      default:
        return unreachableCase(previousClickableCardState)
    }
  } else {
    // it's not my turn, and there are no minor actions etc. that i'm supposed to reply to.
    return {
      mode: 'none',
      inProgressMajorAction: playerGamePosition.inProgressMajorAction,
      pendingMinorActionForMe: undefined,
      rules,
    }
  }
}

export const clickableCardIdsFor = (
  clickableCardState: TClickableCardStateV2
): Set<string> => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      const myHandIds = clickableCardState.clickableCardsInHandIds
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'slyDeal':
          return S.union(
            myHandIds,
            clickableCardState.consideringMajorAction.requestableCardIds
          )
        case 'forcedDeal':
          return S.union(
            myHandIds,
            clickableCardState.consideringMajorAction.requestableCardIds,
            clickableCardState.consideringMajorAction.offerableCardIds
          )
        case 'discard':
          return S.union(
            clickableCardState.clickableCardsInMyPortfolioIds,
            clickableCardState.clickableCardsInHandIds
          )
        case 'needsNeighborhood':
        case 'debtCollector':
        case 'directionalRent':
          return myHandIds
        case undefined:
          return S.union(
            clickableCardState.clickableCardsInHandIds,
            clickableCardState.clickableCardsInMyPortfolioIds
          )
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'paySomething':
    case 'acceptOrJustSayNo':
      return S.create(clickableCardState.clickableCardIds)
    case 'none':
    case 'chooseNeighborhood':
      return new Set()
    default:
      return unreachableCase(clickableCardState)
  }
}

// probably replace / combine this with a check against highlightedCardIdsFor?
export const fadeUnclickableCardsFor = (
  clickableCardState: TClickableCardStateV2
): boolean => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'needsNeighborhood':
        case 'debtCollector':
        case 'directionalRent':
          return false
        case undefined:
          return clickableCardState.playerGamePosition.play === 'discardPlay'
        case 'forcedDeal':
        case 'slyDeal':
        case 'discard':
          return true
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'acceptOrJustSayNo':
      return true
    case 'paySomething':
    case 'chooseNeighborhood':
    case 'none':
      return false
    default:
      return unreachableCase(clickableCardState)
  }
}

export const selectedCardIdsFor = (
  clickableCardState: TClickableCardStateV2
): Set<string> => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'slyDeal':
          return S.create(
            compact([
              clickableCardState.selectedCardInHandId,
              clickableCardState.consideringMajorAction.selectedCardToRequestId,
            ])
          )
        case 'forcedDeal':
          return S.create(
            compact([
              clickableCardState.selectedCardInHandId,
              clickableCardState.consideringMajorAction.selectedCardToRequestId,
              clickableCardState.consideringMajorAction.selectedCardToOfferId,
            ])
          )
        case 'discard':
          return S.union(
            S.createOrEmpty(clickableCardState.selectedCardInHandId),
            S.createOrEmpty(clickableCardState.selectedCardInMyPortfolioId),
            clickableCardState.consideringMajorAction.selectedCardsToDiscardIds
          )
        case 'needsNeighborhood':
        case 'debtCollector':
        case 'directionalRent':
          return S.create(clickableCardState.consideringMajorAction.card.id)
        case undefined:
          return S.union(
            S.createOrEmpty(clickableCardState.selectedCardInHandId),
            S.createOrEmpty(clickableCardState.selectedCardInMyPortfolioId)
          )
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'paySomething':
      return clickableCardState.selectedCardIds
    case 'chooseNeighborhood':
      return new Set()
    case 'none':
      return new Set()
    case 'acceptOrJustSayNo':
      return S.createOrEmpty(clickableCardState.selectedCardInHandId)
    default:
      return unreachableCase(clickableCardState)
  }
}

// TODO WIP. "highlighted" is the opposite of "Faded" like in fadeUnclickableCardsFor
//  basically, we want to showcase which cards are being played/stolen/etc by other players sometimes
//  even if the viewing player can't click those cards
export const highlightedCardIdsFor = (
  clickableCardState: TClickableCardStateV2,
  currentGamePosition: GamePosition
): Set<string> => {
  const emptySet = new Set<string>()

  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      return emptySet
    case 'paySomething':
      return emptySet
    case 'chooseNeighborhood':
      if (clickableCardState.for instanceof MinorActionPromptForcedDeal) {
        return S.union(
          S.create([
            clickableCardState.for.offeredCard.id,
            clickableCardState.for.requestedCard.id,
          ]),
          S.createOrEmpty(
            currentGamePosition instanceof PlayerGamePosition
              ? currentGamePosition.currentPlayerHand.cards
                  .filter(isInstance(JustSayNoCard))
                  .map(c => c.id)
              : undefined
          ),
          S.createOrEmpty(
            currentGamePosition instanceof PlayerGamePosition
              ? currentGamePosition.currentPlayer.portfolio.neighborhoods
                  .filter(pn =>
                    S.has(
                      pn.id,
                      clickableCardState.clickablePortfolioNeighborhoodIds
                    )
                  )
                  .map(pn => pn.propertiesAndResidences().map(c => c.id))
                  .flat()
              : undefined
          )
        )
      } else if (clickableCardState.for instanceof MinorActionPromptJustSayNo) {
        return emptySet
      } else {
        return unreachableCase(clickableCardState.for)
      }
    case 'none':
      if (clickableCardState.inProgressMajorAction) {
        return switchOnMajorAction<Set<string>>({
          whenBirthday: () => emptySet,
          whenChangeNeighborhood: () => emptySet,
          whenDealBreaker: (action: MajorActionDealBreaker) =>
            S.createOrEmpty(
              currentGamePosition.portfolios
                .map(p => p.neighborhoods)
                .flat()
                .find(pn => pn.id === action.portfolioNeighborhoodId)
                ?.propertiesAndResidences()
                .map(c => c.id)
            ),
          whenDebtCollector: () => emptySet,
          whenDirectionalRent: () => emptySet,
          whenDiscard: () => emptySet,
          whenDoubleTheRent: () => emptySet,
          whenForcedDeal: (action: MajorActionForcedDeal) =>
            S.create([action.offeredCard.id, action.requestedCard.id]),
          whenPassGo: () => emptySet,
          whenPlayAsCash: () => emptySet,
          whenPlayToNeighborhood: () => emptySet,
          whenResidenceToCash: () => emptySet,
          whenSlyDeal: (action: MajorActionSlyDeal) =>
            S.create(action.requestedCard.id),
          whenUniversalRent: () => emptySet,
        })(clickableCardState.inProgressMajorAction.majorAction)
      }
      return emptySet
    case 'acceptOrJustSayNo':
      if (clickableCardState.minorActionPrompt instanceof MinorActionPromptSlyDeal) {
        return S.create(clickableCardState.minorActionPrompt.requestedCard.id)
      } else if (
        clickableCardState.minorActionPrompt instanceof MinorActionPromptDealBreaker
      ) {
        const targetPNId =
          clickableCardState.minorActionPrompt.portfolioNeighborhoodId
        return S.createOrEmpty(
          currentGamePosition instanceof PlayerGamePosition
            ? currentGamePosition.currentPlayer.portfolio.neighborhoods
                .find(pn => pn.id === targetPNId)
                ?.propertiesAndResidences()
                .map(c => c.id)
            : undefined
        )
      } else if (
        clickableCardState.minorActionPrompt instanceof MinorActionPromptJustSayNo
      ) {
        return emptySet // idk, maybe this isn't right
      } else {
        return unreachableCase(clickableCardState.minorActionPrompt)
      }
    default:
      return unreachableCase(clickableCardState)
  }
}

export const clickableNeighborhoodIdsFor = (
  clickableCardState: TClickableCardStateV2
): Set<string> => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'needsNeighborhood':
        case 'directionalRent':
          return clickableCardState.consideringMajorAction
            .clickablePortfolioNeighborhoodIds
        case 'discard':
        case 'slyDeal':
        case 'forcedDeal':
        case 'debtCollector':
        case undefined:
          return new Set()
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'paySomething':
    case 'acceptOrJustSayNo':
    case 'none':
      return new Set() // TODO or return undefined if we want to know the difference?
    case 'chooseNeighborhood':
      return clickableCardState.clickablePortfolioNeighborhoodIds
    default:
      return unreachableCase(clickableCardState)
  }
}

export const selectedNeighborhoodIdFor = (
  clickableCardState: TClickableCardStateV2
): string | undefined => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'needsNeighborhood':
        case 'directionalRent':
          return clickableCardState.consideringMajorAction
            .selectedPortfolioNeighborhoodId
        case 'discard':
        case 'slyDeal':
        case 'forcedDeal':
        case 'debtCollector':
        case undefined:
          return undefined
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'paySomething':
    case 'acceptOrJustSayNo':
    case 'none':
      return undefined
    case 'chooseNeighborhood':
      return clickableCardState.selectedPortfolioNeighborhoodId
    default:
      return unreachableCase(clickableCardState)
  }
}

export const clickablePlayerIdsFor = (
  clickableCardState: TClickableCardStateV2
): Set<string> => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'directionalRent':
        case 'debtCollector':
          return clickableCardState.consideringMajorAction.clickablePlayerIds
        case 'needsNeighborhood':
        case 'discard':
        case 'slyDeal':
        case 'forcedDeal':
        case undefined:
          return new Set()
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'paySomething':
    case 'acceptOrJustSayNo':
    case 'none':
    case 'chooseNeighborhood':
      return new Set() // TODO or return undefined if we want to know the difference?
    default:
      return unreachableCase(clickableCardState)
  }
}

export const selectedPlayerIdFor = (
  clickableCardState: TClickableCardStateV2
): string | undefined => {
  switch (clickableCardState.mode) {
    case 'awaitingYourMajorAction':
      switch (clickableCardState.consideringMajorAction?.type) {
        case 'directionalRent':
        case 'debtCollector':
          return clickableCardState.consideringMajorAction.selectedPlayerId
        case 'needsNeighborhood':
        case 'discard':
        case 'slyDeal':
        case 'forcedDeal':
        case undefined:
          return undefined
        default:
          return unreachableCase(clickableCardState.consideringMajorAction)
      }
    case 'paySomething':
    case 'acceptOrJustSayNo':
    case 'none':
    case 'chooseNeighborhood':
      return undefined
    default:
      return unreachableCase(clickableCardState)
  }
}
